import { IonButton, IonIcon } from '@ionic/react';
import { checkmarkCircle } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import { IState } from 'appRedux/createStore';
import { ILinkPlayerInfo } from 'appRedux/models/casinoModels';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { selectUserId } from 'appRedux/selectors';
import { ICasinoLinkedAccount, AuthService } from 'services/auth';
import { CasinoService } from 'services/casino';
import {
  invalidatePlayerScoped,
  linkedAccountsBucket,
  playerScopedKey,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

const PlayerLinkedNotification: React.FC = () => {
  const [showPill, setShowPill] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [casinoName, setCasinoName] = useState('');
  const dismissTimer = useRef<NodeJS.Timeout | null>(null);
  // Accounts captured when the pill is shown so the acknowledge logic still
  // has them after the Redux state updates and verifiedAccounts empties.
  const shownAccounts = useRef<ICasinoLinkedAccount[]>([]);

  const dispatch = useDispatch();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const userId = useSelector(selectUserId);

  const linkPlayerState = useSelector<IState>(
    (state) => state.app.persistedState.linkPlayerState
  ) as ILinkPlayerInfo;

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.casinoId
  ) as number;

  useEffect(() => {
    if (!userId) return;
    const authService = new AuthService();
    const key = playerScopedKey(userId);
    // Once per user session; concurrent mounts share one in-flight GET.
    lastValueFrom(sharedPlayerScopedGet(linkedAccountsBucket, key, () =>
      authService.GetLinkedAccounts(userId)
    ))
      .then((data) => {
        const linkedAccounts = data?.user?.linkedAccounts;
        if (linkedAccounts) {
          casinoCommands.SetLinkedCasinos({ casinoLinkedAccounts: linkedAccounts });
        }
      })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Refresh linked accounts (including acknowledgedDate) when switching casinos.
  // Skipped on mount: the [userId] effect above has just started the same GET,
  // and invalidating here would drop its in-flight entry and fire a second,
  // identical GetLinkedAccounts on every Explore load.
  const isFirstCasinoRun = useRef(true);
  useEffect(() => {
    if (isFirstCasinoRun.current) {
      isFirstCasinoRun.current = false;
      return;
    }
    if (!userId || !casinoId) return;
    const authService = new AuthService();
    const key = playerScopedKey(userId);
    invalidatePlayerScoped(linkedAccountsBucket, key);
    lastValueFrom(sharedPlayerScopedGet(linkedAccountsBucket, key, () =>
      authService.GetLinkedAccounts(userId)
    ))
      .then((data) => {
        const linkedAccounts = data?.user?.linkedAccounts;
        if (linkedAccounts) {
          casinoCommands.SetLinkedCasinos({ casinoLinkedAccounts: linkedAccounts });
        }
      })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casinoId]);

  // Show the pill for the current casino when its linked account is verified
  // and either has never been acknowledged, or was re-verified after the last
  // acknowledgement date.
  const verifiedAccounts = (linkPlayerState?.casinoLinkedAccounts || []).filter(
    (acc: ICasinoLinkedAccount) => {
      if (acc.verification !== 'verified' || acc.casinoId !== casinoId) return false;
      if (!acc.acknowledgedDate) return true;
      if (!acc.verifiedDate) return false;
      return new Date(acc.verifiedDate).getTime() > acc.acknowledgedDate;
    }
  );

  const acknowledge = (accounts: ICasinoLinkedAccount[]) => {
    if (!userId || accounts.length === 0) return;
    const authService = new AuthService();
    const currentAccounts = linkPlayerState?.casinoLinkedAccounts || [];
    accounts.forEach((acc) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      authService.AcknowledgeLinkedAccount(userId, acc.casinoId).subscribe({
        next: (result) => {
          const res = result as { status?: string; acknowledgedDate?: number } | undefined;
          if (res?.acknowledgedDate) {
            // Update Redux so the filter re-evaluates without a full refetch.
            const updated = currentAccounts.map((a) =>
              a.casinoId === acc.casinoId
                ? { ...a, acknowledgedDate: res.acknowledgedDate as number }
                : a
            );
            casinoCommands.SetLinkedCasinos({ casinoLinkedAccounts: updated });
          }
        },
        error: () => undefined,
      });
    });
  };

  useEffect(() => {
    if (verifiedAccounts.length === 0) return;

    shownAccounts.current = verifiedAccounts;
    setShowPill(true);

    dismissTimer.current = setTimeout(() => {
      setShowPill(false);
      acknowledge(shownAccounts.current);
    }, 3000);

    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedAccounts.length, casinoId]);

  const handlePillClick = async () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    setShowPill(false);
    acknowledge(shownAccounts.current);

    const casinoService = new CasinoService();
    const firstVerified = shownAccounts.current[0];
    try {
      const data = await lastValueFrom(casinoService.GetAllCasinos());
      const casino = data?.DataSet?.find((c) => c.casinoId === firstVerified?.casinoId);
      setCasinoName(casino?.displayName || '');
    } catch {
      setCasinoName('');
    }
    setShowModal(true);
  };

  const handleContinue = () => {
    setShowModal(false);
  };

  if (!showPill && !showModal) return null;

  return (
    <>
      {showPill && (
        <button className="player-linked-pill" onClick={() => void handlePillClick()}>
          Player ID Linked!
        </button>
      )}

      {showModal && (
        <div
          className="link-player-result-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="link-player-result-card">
            <IonIcon icon={checkmarkCircle} className="link-player-result-card__icon" />
            <h2 className="link-player-result-card__title">Player ID Linked!</h2>
            <p className="link-player-result-card__subtitle">
              {casinoName
                ? `You've successfully connected your Player ID for ${casinoName}. Enjoy your FREE access to `
                : "You've successfully connected your Player ID. Enjoy your FREE access to "}
              <span className="link-player-result-card__subtitle--pro">PRO</span>
              {' for 30 days!'}
            </p>
            <div className="link-player-result-card__actions">
              <IonButton
                expand="block"
                className="link-player-result-card__btn--primary"
                onClick={handleContinue}
              >
                CONTINUE
              </IonButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlayerLinkedNotification;
